import * as C from './constants';
import { ARCADE_PHASES, SCENE_ORDER, levelById, totalTurns } from './levels';
import { mulberry } from './rng';
import type {
  Grade, LevelDef, Mode, RopeSegment, RunSummary, SceneId, SimConfig, SimEvent, Target, TargetKind, Turn,
} from './types';

const ARCADE_TOTAL = totalTurns(ARCADE_PHASES);
const POWERUPS: TargetKind[] = ['slow', 'bubble', 'magnet'];

/**
 * Deterministic game simulation, stepped at a fixed 120 Hz.
 * The same seed plus the same input log always gives the same result,
 * so the server can replay a run to verify its score.
 */
export class Sim {
  readonly cfg: SimConfig;
  readonly mode: Mode;
  readonly level: LevelDef | undefined;
  readonly ranked: boolean;

  tick = 0;
  /** Rope position in turns. Each whole number is a pass under the frog; .5 is overhead. */
  phase = C.START_PHASE;
  turnIndex = 0;
  turn: Turn;
  pending: Turn | null = null;
  scene: SceneId;
  rng: number;
  cycle = -1;
  cycleF = 1;

  // frog
  y = 0;
  vy = 0;
  air = false;
  held = false;
  holdTicks = 0;
  buffered = -1;
  tongueUntil = -1;
  stunUntil = -1;

  // stats
  score = 0;
  jumps = 0;
  perfects = 0;
  combo = 0;
  bestCombo = 0;
  flies = 0;

  // power-ups
  slowUntil = -1;
  magnetUntil = -1;
  bubble = false;

  targets: Target[] = [];
  nextFlyTick: number;
  nextId = 1;

  over = false;
  won = false;
  events: SimEvent[] = [];
  recordEvents = true;
  /** Flat input log: [tick, kind, tick, kind, ...], kind 1 = press, 0 = release. */
  log: number[] = [];

  constructor(cfg: SimConfig) {
    this.cfg = cfg;
    this.mode = cfg.mode;
    this.ranked = cfg.mode === 'arcade' || cfg.mode === 'daily';
    this.level = cfg.mode === 'adventure' ? levelById(cfg.levelId) : undefined;
    if (cfg.mode === 'adventure' && !this.level) throw new Error('Nivel desconocido: ' + cfg.levelId);
    this.rng = cfg.seed >>> 0;
    this.scene = this.level ? this.level.scene : cfg.mode === 'practice' ? cfg.scene ?? 'pond' : 'pond';
    this.nextFlyTick = 240 + Math.floor(this.rand() * 240);
    this.turn = this.computeTurn(0, false);
  }

  rand(): number {
    const [v, next] = mulberry(this.rng);
    this.rng = next;
    return v;
  }

  // ---------- input ----------

  input(kind: 0 | 1): void {
    if (this.over) return;
    this.log.push(this.tick, kind);
    if (kind === 1) this.press();
    else this.held = false;
  }

  private press(): void {
    this.held = true;
    if (this.stunUntil > this.tick) return;
    if (!this.air) {
      this.jump();
      return;
    }
    if (this.vy < 0 && this.y < C.BUFFER_HEIGHT) {
      this.buffered = this.tick + C.BUFFER_TICKS;
      return;
    }
    if (this.tongueUntil <= this.tick) this.tongue();
  }

  private jump(): void {
    this.air = true;
    this.vy = C.JUMP_V0;
    this.holdTicks = 0;
    this.buffered = -1;
    this.emit({ t: 'jump' });
  }

  private tongue(): void {
    this.tongueUntil = this.tick + C.TONGUE_TICKS;
    const headH = this.y + C.HEAD_H;
    const reach = this.magnetUntil > this.tick ? C.MAGNET_REACH : C.TONGUE_REACH;
    let best = -1;
    let bestD = reach * reach;
    for (let i = 0; i < this.targets.length; i++) {
      const t = this.targets[i];
      const dx = t.x - C.FROG_X;
      const dh = t.h - headH;
      const d = dx * dx + dh * dh;
      if (d <= bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best < 0) {
      this.emit({ t: 'tongue', hit: false, x: C.FROG_X + 30, h: headH + 50 });
      return;
    }
    const t = this.targets[best];
    this.targets.splice(best, 1);
    let points = 0;
    switch (t.kind) {
      case 'fly': points = C.FLY_POINTS; this.flies++; break;
      case 'golden': points = C.GOLDEN_POINTS; this.flies++; break;
      case 'slow': this.slowUntil = this.tick + C.SLOW_TICKS; break;
      case 'magnet': this.magnetUntil = this.tick + C.MAGNET_TICKS; break;
      case 'bubble': this.bubble = true; break;
    }
    this.score += points;
    this.emit({ t: 'tongue', hit: true, x: t.x, h: t.h });
    this.emit({ t: 'catch', kind: t.kind, points, x: t.x, h: t.h });
  }

  // ---------- rope ----------

  /** Rope speed in turns per tick at a given position within the turn. */
  speedAt(frac: number): number {
    let speed = 1 / (this.turn.period * C.TICK_HZ);
    if (this.turn.feint) {
      const d = Math.abs(frac - 0.5) * 5;
      if (d < 1) speed *= 1 - 0.55 * (1 - d);
    }
    if (this.slowUntil > this.tick) speed *= C.SLOW_MUL;
    return speed;
  }

  private segmentAt(i: number): { seg: RopeSegment; local: number; speedF: number } {
    if (this.mode === 'practice') {
      const p = Math.min(2, Math.max(C.MIN_PERIOD, this.cfg.practicePeriod ?? 1.3));
      return { seg: { type: 'steady', period: p, turns: 1 }, local: 0, speedF: 1 };
    }
    const segs = this.level ? this.level.rope : ARCADE_PHASES;
    const total = this.level ? totalTurns(segs) : ARCADE_TOTAL;
    let speedF = 1;
    if (!this.level) {
      const cycle = Math.floor(i / total);
      if (cycle !== this.cycle) {
        let f = 1;
        for (let c = 0; c < cycle; c++) f *= C.CYCLE_SPEEDUP;
        this.cycle = cycle;
        this.cycleF = f;
      }
      speedF = this.cycleF;
    }
    let k = i % total;
    for (const seg of segs) {
      if (k < seg.turns) return { seg, local: k, speedF };
      k -= seg.turns;
    }
    return { seg: segs[0], local: 0, speedF };
  }

  private computeTurn(i: number, allowGust = true): Turn {
    const { seg, local, speedF } = this.segmentAt(i);
    let period = seg.period;
    if (seg.type === 'accel' && seg.to !== undefined) {
      period = seg.period + (seg.to - seg.period) * (seg.turns > 1 ? local / (seg.turns - 1) : 1);
    }
    if (seg.type === 'burst' && local % 5 >= 2) period *= 0.68;
    period *= speedF;
    const turn: Turn = {
      period,
      feint: seg.type === 'feint' && local % 2 === 1,
      double: seg.type === 'double',
      dir: seg.type === 'reverse' ? -1 : 1,
      gust: 0,
    };
    if (allowGust && this.scene === 'storm' && this.rand() < 0.35) {
      turn.gust = this.rand() < 0.5 ? 1 : -1;
      turn.period *= turn.gust === 1 ? 0.84 : 1.18;
    }
    if (this.scene === 'heron' && !turn.double && this.rand() < 0.3) turn.feint = true;
    const min = turn.double ? C.MIN_DOUBLE_PERIOD : C.MIN_PERIOD;
    if (turn.period < min) turn.period = min;
    return turn;
  }

  // ---------- step ----------

  step(): void {
    if (this.over) return;
    const prev = this.phase;
    const frac0 = prev - Math.floor(prev);
    this.phase = prev + this.speedAt(frac0);

    this.updateFrog();
    this.updateTargets();

    const wrapped = Math.floor(this.phase) > Math.floor(prev);
    const frac1 = this.phase - Math.floor(this.phase);
    if (!wrapped && frac0 < 0.5 && frac1 >= 0.5) {
      if (this.turn.double) this.crossing(2);
      if (!this.over) {
        // Decide the next turn half a turn early, so storm gusts can be announced.
        this.pending = this.computeTurn(this.turnIndex + 1);
        if (this.pending.gust !== 0) this.emit({ t: 'gust', dir: this.pending.gust });
      }
    }
    if (wrapped && !this.over) {
      this.crossing(1);
      this.turnIndex++;
      this.turn = this.pending ?? this.computeTurn(this.turnIndex);
      this.pending = null;
      this.emit({ t: 'turn', turn: this.turn });
      if (!this.over && this.turnIndex % 12 === 6 && this.rand() < 0.6) {
        const kind = POWERUPS[Math.floor(this.rand() * POWERUPS.length)];
        this.spawn(kind, 105 + this.rand() * 30, 45);
      }
    }
    this.tick++;
    if (this.tick >= C.MAX_RUN_TICKS) this.over = true;
  }

  private updateFrog(): void {
    if (!this.air) return;
    let g = C.GRAVITY;
    if (this.held && this.vy > 0 && this.holdTicks < C.HOLD_MAX_TICKS) {
      g *= C.HOLD_GRAVITY_MUL;
      this.holdTicks++;
    }
    this.vy -= g * C.DT;
    this.y += this.vy * C.DT;
    if (this.y <= 0) {
      this.y = 0;
      this.vy = 0;
      this.air = false;
      this.emit({ t: 'land' });
      if (this.buffered >= this.tick) this.jump();
      else this.buffered = -1;
    }
  }

  private spawn(kind: TargetKind, h: number, speed: number): void {
    const fromLeft = this.rand() < 0.5;
    this.targets.push({ id: this.nextId++, kind, x: fromLeft ? 20 : 780, h, vx: fromLeft ? speed : -speed });
  }

  private updateTargets(): void {
    if (this.tick >= this.nextFlyTick) {
      const golden = this.rand() < 0.08;
      const h = 90 + this.rand() * 60;
      const speed = 100 + this.rand() * 60;
      this.spawn(golden ? 'golden' : 'fly', h, speed);
      this.nextFlyTick = this.tick + Math.floor((2.5 + this.rand() * 2.5) * C.TICK_HZ);
    }
    const magnet = this.magnetUntil > this.tick;
    const headH = this.y + C.HEAD_H;
    for (let i = this.targets.length - 1; i >= 0; i--) {
      const t = this.targets[i];
      t.x += t.vx * C.DT;
      if (magnet) {
        const dx = t.x - C.FROG_X;
        if (dx > -300 && dx < 300) {
          t.h += (headH - t.h) * 0.03;
          t.x -= dx * 0.01;
        }
      }
      if (t.x < 0 || t.x > 800) this.targets.splice(i, 1);
    }
  }

  private crossing(rope: 1 | 2): void {
    if (this.stunUntil > this.tick) return;
    const th = this.scene === 'ice' ? C.THRESHOLDS.ice : C.THRESHOLDS.normal;
    const y = this.y;
    if (y > th.ok) {
      const grade: Grade = y >= th.perfect ? 'perfect' : y >= th.good ? 'good' : 'ok';
      if (grade === 'perfect') {
        this.perfects++;
        this.combo++;
      } else if (grade === 'ok') {
        this.combo = 0;
      }
      if (this.combo > this.bestCombo) this.bestCombo = this.combo;
      const mult = 1 + Math.min(4, Math.floor(this.combo / 4));
      const points = C.GRADE_POINTS[grade] * mult;
      this.score += points;
      this.jumps++;
      this.emit({ t: 'cross', grade, points, mult, rope, y });
      if (this.ranked) {
        const sc = SCENE_ORDER[Math.floor(this.jumps / C.JUMPS_PER_SCENE) % SCENE_ORDER.length];
        if (sc !== this.scene) {
          this.scene = sc;
          this.emit({ t: 'scene', scene: sc });
        }
      }
      if (this.level && this.jumps >= this.level.goalJumps) {
        this.over = true;
        this.won = true;
        this.emit({ t: 'win' });
      }
      return;
    }
    if (this.bubble) {
      this.bubble = false;
      this.combo = 0;
      this.emit({ t: 'bubble' });
      return;
    }
    this.combo = 0;
    this.emit({ t: 'tangle', fatal: this.mode !== 'practice', y });
    this.y = 0;
    this.vy = 0;
    this.air = false;
    this.buffered = -1;
    if (this.mode === 'practice') this.stunUntil = this.tick + C.STUN_TICKS;
    else this.over = true;
  }

  private emit(e: SimEvent): void {
    if (this.recordEvents) this.events.push(e);
  }

  // ---------- helpers ----------

  clone(): Sim {
    const c = Object.create(Sim.prototype) as Sim;
    Object.assign(c, this);
    c.turn = { ...this.turn };
    c.pending = this.pending ? { ...this.pending } : null;
    c.targets = this.targets.map((t) => ({ ...t }));
    c.events = [];
    c.log = [];
    return c;
  }

  summary(): RunSummary {
    let stars = 0;
    if (this.won && this.level) {
      stars = 1;
      if (this.jumps > 0 && this.perfects / this.jumps >= 0.5) {
        stars = 2;
        if (this.flies >= this.level.fliesFor3) stars = 3;
      }
    }
    return {
      score: this.score,
      jumps: this.jumps,
      perfects: this.perfects,
      bestCombo: this.bestCombo,
      flies: this.flies,
      ticks: this.tick,
      won: this.won,
      stars,
    };
  }
}

// ---------- replay (used by the server to verify a run) ----------

export interface ReplayResult extends RunSummary {
  over: boolean;
  valid: boolean;
}

/** Checks the log shape: integer ticks in order, kinds 0 or 1. */
export function isValidLog(log: readonly unknown[]): log is number[] {
  if (log.length % 2 !== 0) return false;
  let last = 0;
  for (let i = 0; i < log.length; i += 2) {
    const tick = log[i];
    const kind = log[i + 1];
    if (typeof tick !== 'number' || !Number.isInteger(tick) || tick < last || tick > C.MAX_RUN_TICKS) return false;
    if (kind !== 0 && kind !== 1) return false;
    last = tick;
  }
  return true;
}

/** Most presses inside any one-second window. */
export function maxPressesPerSecond(log: readonly number[]): number {
  const presses: number[] = [];
  for (let i = 0; i < log.length; i += 2) if (log[i + 1] === 1) presses.push(log[i]);
  let best = 0;
  let start = 0;
  for (let end = 0; end < presses.length; end++) {
    while (presses[end] - presses[start] >= C.TICK_HZ) start++;
    best = Math.max(best, end - start + 1);
  }
  return best;
}

export function replay(cfg: SimConfig, log: readonly unknown[], maxTicks: number): ReplayResult {
  const sim = new Sim(cfg);
  sim.recordEvents = false;
  if (!isValidLog(log)) return { ...sim.summary(), over: false, valid: false };
  const limit = Math.min(maxTicks, C.MAX_RUN_TICKS);
  let i = 0;
  while (!sim.over && sim.tick < limit) {
    while (i < log.length && log[i] === sim.tick) {
      sim.input(log[i + 1] as 0 | 1);
      i += 2;
    }
    sim.step();
  }
  return { ...sim.summary(), over: sim.over, valid: i === log.length };
}
